<template>
  <div class="card shadow-sm mt-4">
    <!-- Header (Clickable to collapse/expand) -->
    <div
      class="card-header bg-white d-flex justify-content-between align-items-center py-3 cursor-pointer"
      style="cursor: pointer"
      @click="isOpen = !isOpen"
    >
      <h6 class="m-0 font-weight-bold text-primary">
        Revenue by Vessels & Service Locations
      </h6>
      <button
        type="button"
        class="btn btn-sm btn-link text-decoration-none p-0"
      >
        <i
          class="fas"
          :class="isOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
        ></i>
      </button>
    </div>

    <!-- Collapsible Body -->
    <div v-show="isOpen" class="card-body">
      <!-- Filter Inputs - Row 1: Dates, Vessels, Vessel Types -->
      <div class="row align-items-end mb-3">
        <!-- Date From -->
        <div class="col-md-3 col-sm-6 mb-3">
          <label class="small text-muted font-weight-bold mb-1">From</label>
          <input
            v-model="filters.startDate"
            type="date"
            class="form-control form-control-sm"
          />
        </div>

        <!-- Date To -->
        <div class="col-md-3 col-sm-6 mb-3">
          <label class="small text-muted font-weight-bold mb-1">To</label>
          <input
            v-model="filters.endDate"
            type="date"
            class="form-control form-control-sm"
          />
        </div>

        <!-- Vessels MultiSelect Dropdown -->
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="multiselect-align-fix">
            <label class="small text-muted font-weight-bold mb-1"
              >Vessels</label
            >
            <multi-select-dropdown
              v-model="filters.vessels"
              :field="vesselsField"
              :margin-bottom="0"
            />
          </div>
        </div>

        <!-- Vessel Types MultiSelect Dropdown -->
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="multiselect-align-fix">
            <label class="small text-muted font-weight-bold mb-1"
              >Vessel Types</label
            >
            <multi-select-dropdown
              v-model="filters.vesselTypes"
              :field="vesselTypesField"
              :margin-bottom="0"
            />
          </div>
        </div>
      </div>

      <!-- Filter Inputs - Row 2: Countries, Ports, Action Button -->
      <div class="row align-items-end mb-4">
        <!-- Vessel Country MultiSelect -->
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="multiselect-align-fix">
            <label class="small text-muted font-weight-bold mb-1"
              >Vessel Countries</label
            >
            <multi-select-dropdown
              v-model="filters.vesselCountries"
              :field="vesselCountryField"
              :margin-bottom="0"
            />
          </div>
        </div>

        <!-- Vessel Base Port Text Input -->
        <div class="col-md-2 col-sm-6 mb-3 mb-md-0">
          <label class="small text-muted font-weight-bold mb-1"
            >Vessel Port</label
          >
          <input
            v-model="filters.vesselPort"
            type="text"
            class="form-control form-control-sm"
            placeholder="Search vessel port..."
          />
        </div>

        <!-- Service Country MultiSelect -->
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="multiselect-align-fix">
            <label class="small text-muted font-weight-bold mb-1"
              >Service Countries</label
            >
            <multi-select-dropdown
              v-model="filters.serviceCountries"
              :field="serviceCountryField"
              :margin-bottom="0"
            />
          </div>
        </div>

        <!-- Service Port Text Input -->
        <div class="col-md-2 col-sm-6 mb-3 mb-md-0">
          <label class="small text-muted font-weight-bold mb-1"
            >Service Port</label
          >
          <input
            v-model="filters.servicePort"
            type="text"
            class="form-control form-control-sm"
            placeholder="Search service port..."
          />
        </div>

        <!-- Trigger Button -->
        <div class="col-md-2 col-sm-12">
          <button
            type="button"
            class="btn btn-primary btn-sm btn-block"
            :disabled="loading"
            @click="fetchRevenue"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            Display Revenue
          </button>
        </div>
      </div>

      <!-- Validation Error Message -->
      <div v-if="validationError" class="alert alert-warning py-2 small mb-3">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        {{ validationError }}
      </div>

      <!-- Results Display Section -->
      <div v-if="loading" class="text-center py-4">
        <div
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        ></div>
      </div>

      <div v-else-if="revenueData" class="mt-3">
        <!-- Total Revenue Summary Card & Toggle Button Row -->
        <div class="row align-items-center mb-3">
          <div class="col-md-4">
            <div class="p-3 bg-primary text-white rounded border text-center">
              <span class="d-block text-white-50 small font-weight-bold mb-1">
                TOTAL NET REVENUE
              </span>
              <h5 class="m-0 font-weight-bold">
                {{ formatCurrency(revenueData.total_net_amount) }}
              </h5>
            </div>
          </div>

          <div class="col-md-8 text-md-right mt-3 mt-md-0">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              @click="showDetails = !showDetails"
            >
              <i
                class="fas mr-1"
                :class="showDetails ? 'fa-eye-slash' : 'fa-eye'"
              ></i>
              {{
                showDetails
                  ? "Hide Detailed Invoices"
                  : "Show Detailed Invoices"
              }}
            </button>
          </div>
        </div>

        <!-- Detailed Breakdown Table Section -->
        <div v-if="showDetails" class="mt-4 border-top pt-3">
          <!-- Toolbar & Export Buttons -->
          <div
            class="d-flex justify-content-between align-items-center mb-3 flex-wrap"
          >
            <h6 class="font-weight-bold text-dark m-0">
              Invoice Records ({{ revenueRows.length }})
            </h6>

            <table-export-buttons
              v-if="revenueRows.length > 0"
              :columns="columnConfig"
              :data="revenueRows"
              file-name="revenue_by_vessel_report"
              title="Vessel Revenue Invoices Report"
            />
          </div>

          <!-- Data Table -->
          <div class="table-scroll-container border rounded">
            <table
              class="table table-sm table-hover table-striped revenue-table m-0"
            >
              <thead class="thead-light">
                <tr>
                  <th
                    v-for="col in columnConfig"
                    :key="col.key"
                    class="small text-nowrap"
                  >
                    {{ col.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="paginatedRows.length === 0">
                  <td
                    :colspan="columnConfig.length"
                    class="text-center text-muted py-3 small"
                  >
                    No rows to display.
                  </td>
                </tr>
                <tr
                  v-for="(row, idx) in paginatedRows"
                  :key="row.invoice_id || idx"
                >
                  <td
                    v-for="col in columnConfig"
                    :key="col.key"
                    class="small align-middle"
                    :title="
                      col.formatter
                        ? col.formatter(row[col.key], row)
                        : row[col.key]
                    "
                  >
                    {{
                      col.formatter
                        ? col.formatter(row[col.key], row)
                        : row[col.key] || "-"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination Controls -->
          <div
            v-if="totalPages > 1"
            class="d-flex justify-content-between align-items-center mt-3"
          >
            <span class="small text-muted">
              Showing {{ pageStartItem }} to {{ pageEndItem }} of
              {{ revenueRows.length }} entries
            </span>

            <ul class="pagination pagination-sm m-0">
              <li
                class="page-item"
                :class="{ disabled: currentPage === 1 }"
                @click="currentPage > 1 && currentPage--"
              >
                <button class="page-link" type="button">Previous</button>
              </li>

              <li
                v-for="page in totalPages"
                :key="page"
                class="page-item"
                :class="{ active: currentPage === page }"
                @click="currentPage = page"
              >
                <button class="page-link" type="button">{{ page }}</button>
              </li>

              <li
                class="page-item"
                :class="{ disabled: currentPage === totalPages }"
                @click="currentPage < totalPages && currentPage++"
              >
                <button class="page-link" type="button">Next</button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Initial Empty State -->
      <div v-else-if="hasSearched" class="text-center py-3 text-muted small">
        No revenue records found for the selected criteria.
      </div>
    </div>
  </div>
</template>

<script>
import MultiSelectDropdown from "@/components/ui/MultiSelectDropdown.vue";
import TableExportButtons from "@/components/tableExport/TableExportButtons.vue";
import { CountryOptions } from "@/constants/address";
import { getVesselTypes, getVesselInvoices } from "@/apis/vessels";

export default {
  name: "VesselsInvoices",
  components: {
    MultiSelectDropdown,
    TableExportButtons,
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      hasSearched: false,
      showDetails: false,
      validationError: "",
      currentPage: 1,
      itemsPerPage: 20,
      vesselOptions: [],
      vesselTypeOptions: [],
      countryOptionsList: [],
      filters: {
        startDate: "",
        endDate: "",
        vessels: [],
        vesselTypes: [],
        vesselCountries: [],
        vesselPort: "",
        serviceCountries: [],
        servicePort: "",
      },
      revenueData: null,
      columnConfig: [
        { label: "Invoice Number", key: "invoice_number" },
        {
          label: "Invoice Date",
          key: "invoice_date",
          formatter: (val) => this.formatDate(val),
        },
        {
          label: "Due Date",
          key: "due_date",
          formatter: (val) => this.formatDate(val),
        },
        {
          label: "Net Amount",
          key: "net_amount",
          formatter: (val) => this.formatCurrency(val),
        },
      ],
    };
  },
  computed: {
    vesselsField() {
      return {
        name: "vessels",
        placeholder: "Select vessels...",
        options: this.vesselOptions,
      };
    },

    vesselTypesField() {
      return {
        name: "vesselTypes",
        placeholder: "Select vessel types...",
        options: this.vesselTypeOptions,
      };
    },

    vesselCountryField() {
      return {
        name: "vesselCountries",
        placeholder: "Select vessel countries...",
        options: this.countryOptionsList,
      };
    },

    serviceCountryField() {
      return {
        name: "serviceCountries",
        placeholder: "Select service countries...",
        options: this.countryOptionsList,
      };
    },

    revenueRows() {
      return this.revenueData?.invoices || [];
    },

    totalPages() {
      return Math.ceil(this.revenueRows.length / this.itemsPerPage) || 1;
    },

    paginatedRows() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.revenueRows.slice(start, end);
    },

    pageStartItem() {
      if (this.revenueRows.length === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    pageEndItem() {
      const end = this.currentPage * this.itemsPerPage;
      return end > this.revenueRows.length ? this.revenueRows.length : end;
    },
  },
  async mounted() {
    this.initCountryOptions();
    await Promise.all([
      this.fetchVesselOptions(),
      this.fetchVesselTypeOptions(),
    ]);
  },
  methods: {
    initCountryOptions() {
      const rawCountries = Array.isArray(CountryOptions) ? CountryOptions : [];
      this.countryOptionsList = rawCountries.map((c) => ({
        text: c.text,
        value: c.value,
      }));
    },
    async fetchVesselOptions() {
      try {
        const response = await this.$store.dispatch(
          "vesselStore/fetchVesselsWithCache",
          {}
        );
        const list = response?.data || response || [];
        this.vesselOptions = list
          .map((item) => ({
            text: item.name,
            value: item.id,
          }))
          .sort((a, b) => (a.text || "").localeCompare(b.text || ""));
      } catch (error) {
        console.error("Failed to load vessels options:", error);
      }
    },

    async fetchVesselTypeOptions() {
      try {
        const response = await getVesselTypes();
        const list = response?.data || response || [];

        this.vesselTypeOptions = list
          .map((item) => ({
            text: item.type,
            value: item.id,
          }))
          .sort((a, b) => (a.text || "").localeCompare(b.text || ""));
      } catch (error) {
        console.error("Failed to load vessel types options:", error);
      }
    },

    validateFilters() {
      const hasStartDate = Boolean(this.filters.startDate);
      const hasEndDate = Boolean(this.filters.endDate);
      const hasVessels =
        this.filters.vessels && this.filters.vessels.length > 0;
      const hasVesselTypes =
        this.filters.vesselTypes && this.filters.vesselTypes.length > 0;
      const hasVesselCountries =
        this.filters.vesselCountries && this.filters.vesselCountries.length > 0;
      const hasVesselPort = Boolean(
        this.filters.vesselPort && this.filters.vesselPort.trim()
      );
      const hasServiceCountries =
        this.filters.serviceCountries &&
        this.filters.serviceCountries.length > 0;
      const hasServicePort = Boolean(
        this.filters.servicePort && this.filters.servicePort.trim()
      );

      return (
        hasStartDate ||
        hasEndDate ||
        hasVessels ||
        hasVesselTypes ||
        hasVesselCountries ||
        hasVesselPort ||
        hasServiceCountries ||
        hasServicePort
      );
    },

    async fetchRevenue() {
      this.validationError = "";

      if (!this.validateFilters()) {
        this.validationError =
          "Please select or specify at least one filter before fetching revenue.";
        return;
      }

      this.loading = true;
      this.hasSearched = true;
      this.currentPage = 1;

      try {
        const payload = {};

        if (this.filters.startDate) payload.startDate = this.filters.startDate;
        if (this.filters.endDate) payload.endDate = this.filters.endDate;

        if (this.filters.vessels?.length > 0) {
          payload.vessel_ids = this.filters.vessels
            .map((v) => v.value)
            .join(",");
        }

        if (this.filters.vesselTypes?.length > 0) {
          payload.vesselTypes = this.filters.vesselTypes
            .map((v) => v.value)
            .join(",");
        }
        if (this.filters.vesselCountries?.length > 0) {
          payload.vesselCountries = this.filters.vesselCountries
            .map((v) => v.value)
            .join(",");
        }

        if (this.filters.vesselPort?.trim()) {
          payload.vesselPort = this.filters.vesselPort.trim();
        }

        if (this.filters.serviceCountries?.length > 0) {
          payload.serviceCountries = this.filters.serviceCountries
            .map((v) => v.value)
            .join(",");
        }

        if (this.filters.servicePort?.trim()) {
          payload.servicePort = this.filters.servicePort.trim();
        }

        const response = await getVesselInvoices(payload);
        this.revenueData = response?.data || response;
      } catch (error) {
        console.error("Failed to fetch vessel revenue report:", error);
        this.revenueData = null;
      } finally {
        this.loading = false;
      }
    },

    formatCurrency(value) {
      const amount = Number(value) || 0;
      return new Intl.NumberFormat("en-IE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
      }).format(amount);
    },

    formatDate(dateVal) {
      if (!dateVal) return "-";
      const date = new Date(dateVal);
      if (isNaN(date.getTime())) return dateVal;

      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.multiselect-align-fix {
  position: relative;
  top: 9px;
}

/* Enforce strict container width with horizontal scrolling */
.table-scroll-container {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Table column handling */
.revenue-table {
  width: 100%;
  min-width: 700px;
  table-layout: auto;
}

.revenue-table th,
.revenue-table td {
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
